import { Auth } from 'aws-amplify';
import React, { useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';

import { isJestTest } from '../../test-helpers/isJestTest';
import SignUpFlowEmail from './SignUpFlowEmail';
import SignUpFlowPassword from './SignUpFlowPassword';
import SignUpFlowUsername from './SignUpFlowUsername';
import VerifyCode from './VerifyCode';

export enum SignUpSteps {
  USERNAME = 'username',
  VERIFY_CODE = 'verifycode',
}

const SignUpFlow: React.FC = () => {
  const signUpFlowPasswordRef = useRef<{ signUpFormSubmit: () => void }>();
  const [SignUpStep, setSignUpStep] = useState<SignUpSteps>(SignUpSteps.USERNAME);
  const [email, setEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userNameError, setUserNameError] = useState<string>('');

  const updateEmail = (emailId: string) => {
    setEmail(emailId);
  };

  const updateUserName = (userId: string) => {
    setUserName(userId);
  };

  const updateEmailError = () => {
    setSignUpStep(SignUpSteps.USERNAME);
  };

  const updateUserNameError = () => {
    setUserNameError('');
  };

  const goToStep = (step: SignUpSteps) => {
    setSignUpStep(step);
  };

  const postSignUp = async () => {
    goToStep(SignUpSteps.VERIFY_CODE);
    return '';
  };

  const attemptUserSignUp = async (password: string): Promise<string> => {
    const username = userName;
    try {
      if (!isJestTest())
        await Auth.signUp({
          username,
          password,
          attributes: { email },
        });
    } catch (err) {
      const error = err as { code: string; message: string; name: string };
      if ('code' in error) {
        switch (error.code) {
          case 'InvalidPasswordException':
            return 'The password should have minimum 8 characters, in that please include one uppercase, one lowercase, one numeric and one special character.';
          case 'CodeMismatchException':
            return 'Invalid verification code provided, please try again.';
          case 'LimitExceededException':
            return 'Attempt limit exceeded, please try after after 15 minute';
          case 'UserNotFoundException':
            return 'Account not found.';
          case 'UsernameExistsException':
            setUserNameError('Account already Exists with same username.');
            return '';
          case 'NotAuthorizedException':
            if ('message' in error && error.message === 'User is disabled.') {
              return 'Account disabled.';
            }
            if (
              'message' in error &&
              error.message ===
                'Temporary password has expired and must be reset by an administrator.'
            ) {
              return 'Your temporary password has expired, please contact your Nutrien Representative for assistance.';
            }
            return 'The username or password is incorrect. Try again or contact IT.';
          case 'PasswordResetRequiredException':
            if ('message' in error && error.message === 'Password reset required for the user') {
              return 'Password Expired. Use the Forgot Password link below to reset it.';
            }
        }
      } else {
        return 'An unknown error occurred.';
      }
    }
    return (await postSignUp()) || '';
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (signUpFlowPasswordRef.current) {
      signUpFlowPasswordRef.current.signUpFormSubmit();
    }
  };

  const getSignUpStep = () => {
    switch (SignUpStep) {
      case SignUpSteps.USERNAME:
        return (
          <form onSubmit={handleOnSubmit}>
            <div>
              <SignUpFlowUsername
                updateUserName={updateUserName}
                userNameError={userNameError}
                updateUserNameError={updateUserNameError}
              />
              <SignUpFlowEmail updateEmail={updateEmail} />
              <SignUpFlowPassword
                ref={signUpFlowPasswordRef}
                attemptUserSignUp={attemptUserSignUp}
                email={email}
                userName={userName}
                updateEmailError={updateEmailError}
              />
            </div>
          </form>
        );
      case SignUpSteps.VERIFY_CODE:
        return <VerifyCode goToStep={goToStep} userName={userName} email={email} />;
      default:
        return <></>;
    }
  };

  return <>{getSignUpStep()}</>;
};

export default withRouter(SignUpFlow);
